import { useMemo } from 'react';
import useVersionManagementV1 from './useVersionManagementV1';
import { parseUnitTestingSections } from '../utils/markdownSectionParser';

/**
 * Custom hook to manage unit testing versions
 * Stores and tracks different versions of unit tests from regenerations
 *
 * Uses useVersionManagementV1 which fixes:
 * - Local edit detection via isLocalUpdateRef
 * - Proper version tracking without creating duplicates on edit
 */
const useUnitTestingVersions = (conversation, isStreaming, streamingContent, isLocalUpdateRef = null) => {
  // Configuration for unit testing version management
  const unitTestingConfig = {
    // Determine if a message contains unit testing content
    isRelevantMessage: (msg) => {
      const content = msg.content || '';
      // More specific matching to avoid false positives
      const hasUnitTestingKeywords =
             content.includes('Testing Strategy') ||
             content.includes('Component Tests') ||
             content.includes('API Tests') ||
             content.includes('Test Cases') ||
             content.includes('Unit Test') ||
             content.includes('unit test') ||
             content.includes('Test Coverage') ||
             content.includes('Mock') ||
             content.includes('Jest') ||
             content.includes('describe(') ||
             content.includes('it(') ||
             content.includes('expect(');
      // Must have unit testing keywords, not just any markdown headers
      return hasUnitTestingKeywords;
    },

    // Parse unit testing sections from content
    parseContent: (content, msg) => {
      if (msg?.parsed_content?.sections?.length) {
        return msg.parsed_content.sections;
      }
      return parseUnitTestingSections(content);
    },

    // Extract version information from a message
    // Note: The id will be set by useVersionManagementV1 based on messageIndex
    extractVersions: (msg, parsedTestCases, versionNumber, isPreview = false) => {
      return {
        id: null, // Will be overwritten by useVersionManagementV1 with messageIndex-based ID
        timestamp: msg.createdAt || new Date().toISOString(),
        testCases: parsedTestCases,
        content: msg.content,
        versionNumber: versionNumber,
        isPreview: isPreview,
        label: isPreview ? 'Generating...' : undefined
      };
    },

    conversation,
    isStreaming,
    streamingContent,
    isLocalUpdateRef
  };

  // Use the V1 version management hook that supports isLocalUpdateRef
  const versionData = useVersionManagementV1(unitTestingConfig);

  // Memoize the currentVersion to prevent unnecessary re-renders
  const currentVersion = useMemo(() => {
    if (!versionData.currentVersion) return null;
    return {
      ...versionData.currentVersion,
      testCases: versionData.currentVersion.parsedContent || versionData.currentVersion.testCases
    };
  }, [versionData.currentVersion]);

  // Map the generic version data to unit testing-specific structure
  return {
    versions: versionData.versions,
    currentVersion,
    currentVersionIndex: versionData.currentVersionIndex,
    setCurrentVersionIndex: versionData.setCurrentVersionIndex,
    hasVersions: versionData.hasVersions,
    isLatestVersion: versionData.isLatestVersion,
    addVersion: versionData.addVersion,
    updateCurrentVersion: versionData.updateCurrentVersion
  };
};

export default useUnitTestingVersions;
